/**
 * PlatformSwitcher — lets a multi-role owner/admin flip between the platforms
 * their workspace is entitled to (Owner dashboard, Sales, Clients, Tax) from
 * the sidebar. Members are pinned to their assigned platform (see
 * PlatformGuard), so this only renders for owners/admins — and only when there
 * is more than one platform to switch to.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useMe } from '../../lib/hooks/use-me';

type Platform = { key: string; label: string; desc: string; to: string; match: (p: string) => boolean; dot: string };

const PLATFORMS: Platform[] = [
  { key: 'owner', label: 'Owner', desc: 'Full business dashboard', to: '/dashboard', match: p => p.startsWith('/dashboard'), dot: 'bg-primary' },
  { key: 'sales', label: 'Sales', desc: 'Pipeline, deals & coaching', to: '/sales', match: p => p.startsWith('/sales'), dot: 'bg-emerald-500' },
  { key: 'clients', label: 'Clients', desc: 'Roster, health & follow-ups', to: '/clients', match: p => p.startsWith('/clients'), dot: 'bg-violet-500' },
  { key: 'tax', label: 'Tax', desc: 'Clients, planning & deadlines', to: '/tax', match: p => p.startsWith('/tax'), dot: 'bg-teal-500' },
];

export function PlatformSwitcher() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Only multi-role owners/admins switch platforms; members are pinned.
  if (me.role !== 'owner' && me.role !== 'admin') return null;

  // Only the products this workspace is entitled to (me.features: owner /
  // sales / clients derive from accounts.products; tax is a separately-sold
  // add-on). A single-product workspace has nothing to switch between.
  const f = me.features;
  const platforms = PLATFORMS.filter(p =>
    (p.key === 'owner' && f?.owner !== false) ||
    (p.key === 'sales' && f?.sales !== false) ||
    (p.key === 'clients' && f?.clients !== false) ||
    (p.key === 'tax' && f?.tax === true),
  );
  if (platforms.length < 2) return null;
  const current = platforms.find(p => p.match(location.pathname)) ?? platforms[0]!;

  return (
    <div ref={ref} className="relative mx-3 mb-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-2 rounded-lg border border-border bg-surface-1 px-3 py-2 text-left transition-colors hover:bg-surface-2"
        title="Switch platform"
      >
        <span className={clsx('h-2 w-2 shrink-0 rounded-full', current.dot)} />
        <div className="min-w-0 flex-1">
          <div className="text-2xs text-ink-dim leading-none">Platform</div>
          <div className="text-xs font-semibold text-ink">{current.label}</div>
        </div>
        <svg className={clsx('text-ink-dim transition-transform', open && 'rotate-180')} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-modal mt-1 overflow-hidden rounded-xl border border-border bg-surface-0 shadow-2xl">
          {platforms.map(p => {
            const active = p.key === current.key;
            return (
              <button
                key={p.key}
                onClick={() => { setOpen(false); if (!active) navigate(p.to); }}
                className={clsx('flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors', active ? 'bg-surface-2' : 'hover:bg-surface-1')}
              >
                <span className={clsx('h-2 w-2 shrink-0 rounded-full', p.dot)} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-ink">{p.label}</div>
                  <div className="text-2xs text-ink-dim truncate">{p.desc}</div>
                </div>
                {active && (
                  <svg className="text-ink-dim" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
